import 'core-js/actual'
import 'regenerator-runtime/runtime'

// Block: fixed imports
import 'moment/locale/es'
// Block: fixed imports

import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import moment from 'moment'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import ReactQueryRewind from 'react-query-rewind'
import { Provider } from 'react-redux'
import * as z from 'zod'

import { NOTIFICATIONS_STATUS } from 'notifications'
import System from 'system'
import { queryClient } from 'utils'

import { es } from 'zod/locales'

import store from '../config/store'
import Routes from './components/Routes'
import WidgetRoutes from './components/WidgetRoutes'
import sentryRegistration from './sentryRegistration'
import serviceWorkerRegistration from './serviceWorkerRegistration'

import type { Workbox } from 'workbox-window'

moment.locale('es')
z.config(es())

const root = createRoot(document.getElementById('app') as HTMLElement)

const USE_STRICT_MODE = process.env.NODE_ENV !== 'production'

const App = () => {
  const widget = document.location.pathname.startsWith('/widgets/')

  if (widget) {
    document.body.classList.add('widget')
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        {widget ? <WidgetRoutes /> : <Routes />}
        <ReactQueryDevtools initialIsOpen={false} />
      </Provider>
      <ReactQueryRewind />
    </QueryClientProvider>
  )
}

const renderApp = () => {
  root.render(
    USE_STRICT_MODE ? (
      <StrictMode>
        <App />
      </StrictMode>
    ) : (
      <App />
    ),
  )
}

if (
  process.env.NODE_ENV === 'production' &&
  !window.location.href.startsWith(System.url) &&
  !window.location.href.match(/(localhost|\.\d{1,3}):505\d/) &&
  !window.location.hostname.match(/\.pages\.dev$/) // cloudflare pages
) {
  window.location.href = System.url
}

// If you are not using sentry, remove this complete if to reduce the bundle size
if (process.env.NODE_ENV === 'production' && System.sentry?.enabled) {
  sentryRegistration()
}

serviceWorkerRegistration({
  onNewVersionFound: ({ reloadClients }: { reloadClients: () => void }) => {
    System.addNotification({
      id: 'system-update',
      buttons: [
        {
          icon: 'refresh',
          onClick: () => reloadClients(),
          text: 'Actualizar',
        },
      ],
      children: (
        <div>
          Hay una nueva versión del sistema
          <br />
          <small>
            {' '}
            - Es necesario <b>recargar la página</b>
          </small>
        </div>
      ),
      dismissAfter: 0,
      icon: 'gift',
      status: NOTIFICATIONS_STATUS.WARNING,
      title: 'Actualización disponible',
    })
  },
  onRegistered: ({
    registration,
    reloadClients,
    workbox,
  }: {
    registration?: ServiceWorkerRegistration
    reloadClients: () => void
    workbox: Workbox
  }) => {
    System.workbox = workbox || {}
    Object.assign(System.workbox, {
      registration,
      reloadClients,
    })
  },
})

renderApp()
